import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, output, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { TextModule } from '@library/ui/atoms/text/text.module';
import { PipesModule } from '@library/utils/pipes/pipes.module';
import { Station } from '@store/weathermap.store';
import { filter, map } from 'rxjs';
import { SelectedStationService } from 'src/app/services/selected-station.service';
import { UserDataService } from 'src/app/services/user-data.service';
import { StationComponent } from 'src/app/toolbar/dashboard/station/station.component';
import { ToolbarSectionComponent } from 'src/app/toolbar/toolbar-panel/toolbar-section/toolbar-section.component';

function arrayToMapId<T extends { id: string }>(array: T[]): Record<string, T> {
  return array.reduce(
    (acc, item) => {
      acc[item.id] = item;
      return acc;
    },
    {} as Record<string, T>,
  );
}

@Component({
  selector: 'app-dashboard',
  imports: [StationComponent, TextModule, NgTemplateOutlet, ToolbarSectionComponent, PipesModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent {
  private readonly homesFacade = inject(HomesFacade);
  private readonly modulesFacade = inject(ModulesFacade);
  private readonly userDataService = inject(UserDataService);
  private readonly selectedStationService = inject(SelectedStationService);

  homes = toSignal(
    this.homesFacade.homes$.pipe(
      filter((homes) => homes.length > 0),
      map(arrayToMapId),
    ),
  );
  stations = toSignal(this.modulesFacade.getModulesByType(['NAMain']).pipe(map(arrayToMapId)));

  outdoorModules = this.userDataService.outdoorModules;

  adminOutdoorModules = computed(() => this.outdoorModules().filter((module) => !this.homes()[module.homeId]?.mode));
  friendOutdoorModules = computed(() =>
    this.outdoorModules().filter((module) => this.homes()[module.homeId]?.mode === 'friend'),
  );
  favoriteOutdoorModules = computed(() =>
    this.outdoorModules().filter((module) => this.homes()[module.homeId]?.mode === 'favorite'),
  );

  close = output();

  selectStation({ station, coordinates }: { station: Station; coordinates: number[] }): void {
    this.selectedStationService.selectStation(station, coordinates);
    window.map.flyTo({ center: [coordinates[0], coordinates[1]], zoom: 13, duration: 1500 });
    this.close.emit();
  }
}
